import { useLayoutEffect, useRef, type RefObject } from 'react';

const overlays: HTMLElement[] = [];
let originalOverflow = '';
const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((element) => {
    const style = getComputedStyle(element);
    return (
      !element.closest('[hidden], [inert], [aria-hidden="true"]') &&
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      element.tabIndex >= 0
    );
  });
}

/** Shared focus lifecycle for nested portalled dialogs and navigation drawers. */
export function useOverlayFocus(
  open: boolean,
  ref: RefObject<HTMLElement | null>,
  onClose: () => void,
  dismissOnEscape: boolean,
): void {
  const latest = useRef({ onClose, dismissOnEscape });
  useLayoutEffect(() => {
    latest.current = { onClose, dismissOnEscape };
  }, [onClose, dismissOnEscape]);

  useLayoutEffect(() => {
    const container = ref.current;
    if (!open || !container) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (overlays.length === 0) {
      originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    overlays.push(container);
    container.focus({ preventScroll: true });

    const isTop = () => overlays[overlays.length - 1] === container;
    const handleFocus = (event: FocusEvent) => {
      // A popover opened from inside this overlay renders in a portal, outside the
      // container. It must keep focus, or its search box can't be typed in.
      const inPopover =
        event.target instanceof Element && event.target.closest('[data-overlay-popover]');
      if (
        isTop() &&
        event.target instanceof Node &&
        !container.contains(event.target) &&
        !inPopover
      ) {
        container.focus({ preventScroll: true });
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (!isTop() || event.defaultPrevented) return;
      if (event.key === 'Escape' && latest.current.dismissOnEscape) {
        event.preventDefault();
        event.stopPropagation();
        latest.current.onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusableElements(container);
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!first || !last) {
        event.preventDefault();
        container.focus();
      } else if (active === container || !container.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('focusin', handleFocus);
    document.addEventListener('keydown', handleKey);
    return () => {
      const wasTop = isTop();
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('keydown', handleKey);
      const index = overlays.indexOf(container);
      if (index !== -1) overlays.splice(index, 1);
      if (overlays.length === 0) document.body.style.overflow = originalOverflow;
      if (wasTop && opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, [open, ref]);
}
